import React, { useState } from 'react';
import { useVendorInvoices, useLinkedCustomers } from '@api/hooks/useVendorPortal';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { FileText } from 'lucide-react';
import type { LinkedCustomerResponse, VendorInvoiceResponse } from '@schemas/api';

const STATUS_COLORS: Record<string, string> = {
  Received: 'bg-gray-100 text-gray-700',
  Processing: 'bg-yellow-100 text-yellow-800',
  Approved: 'bg-blue-100 text-blue-800',
  Paid: 'bg-green-100 text-green-800',
  Rejected: 'bg-red-100 text-red-800',
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  'Pending Approval': 'bg-yellow-100 text-yellow-800',
  'Approved': 'bg-blue-100 text-blue-800',
  'Paid': 'bg-green-100 text-green-800',
  'Cancelled': 'bg-red-100 text-red-800',
};

const PAGE_SIZE = 50;
const VENDOR_INVOICE_STATUS_OPTIONS = [
  { id: '', label: 'All Statuses' },
  { id: 'Received', label: 'Received' },
  { id: 'Processing', label: 'Processing' },
  { id: 'Approved', label: 'Approved' },
  { id: 'Rejected', label: 'Rejected' },
];

export default function VendorInvoicesPage() {
  const [skip, setSkip] = useState(0);
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const { data: customers } = useLinkedCustomers();
  const { data, isLoading } = useVendorInvoices(skip, PAGE_SIZE, customerId, statusFilter);

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-content-primary dark:text-content-primary-dark">
          Invoices
        </h1>
        <p className="mt-1 text-content-secondary dark:text-content-secondary-dark">
          Processing status of invoices submitted to your customers.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <SearchableSelect
          options={[
            { id: '', label: 'All Customers' },
            ...(customers?.items ?? []).map((customer: LinkedCustomerResponse) => ({
              id: customer.customer_tenant_id,
              label: customer.customer_name,
            })),
          ]}
          value={customerId ?? ''}
          onChange={(value) => {
            setCustomerId(value || undefined);
            setSkip(0);
          }}
          placeholder="All Customers"
          clearable={false}
          className="w-56"
        />
        <SearchableSelect
          options={VENDOR_INVOICE_STATUS_OPTIONS}
          value={statusFilter ?? ''}
          onChange={(value) => {
            setStatusFilter(value || undefined);
            setSkip(0);
          }}
          placeholder="All Statuses"
          clearable={false}
          className="w-48"
        />
      </div>

      {isLoading ? (
        <div className="text-gray-500 p-6">Loading invoices...</div>
      ) : !data?.items.length ? (
        <div className="text-center py-12 bg-white dark:bg-surface-base-dark rounded-lg border border-border-default">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No invoices found.</p>
          <p className="text-sm text-gray-500 mt-1">
            Invoices submitted to your customers will appear here once they are processed.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-surface-base-dark rounded-lg border border-border-default overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.items.map((invoice: VendorInvoiceResponse) => (
                <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 font-mono text-sm text-gray-900 dark:text-white">
                    {invoice.invoice_number}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                    {invoice.customer_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(invoice.invoice_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right font-medium">
                    {formatAmount(invoice.amount, invoice.currency)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[invoice.status] || 'bg-gray-100 text-gray-600'}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {invoice.payment_status ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PAYMENT_STATUS_COLORS[invoice.payment_status] || 'bg-gray-100 text-gray-600'}`}>
                        {invoice.payment_status}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                disabled={skip === 0}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setSkip(skip + PAGE_SIZE)}
                disabled={skip + PAGE_SIZE >= data.total}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
